import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Calendar, Copy, Send, RefreshCcw, Ban, Video, Loader2, Award } from "lucide-react";
const brl = (n) => Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const WEEKDAYS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

const TIER_CHIP = {
  ouro:   "bg-gradient-to-r from-[#F1C232] to-[#D4A017] text-[#3B2F0B]",
  prata:  "bg-gradient-to-r from-slate-300 to-slate-500 text-slate-900",
  bronze: "bg-gradient-to-r from-[#CD7F32] to-[#8B4513] text-white",
};

/**
 * Widget do painel do consultor para AGENDAR consultas com profissionais
 * credenciados. Fluxo:
 *  1. Consultor escolhe profissional → sistema mostra agenda semanal.
 *  2. Consultor escolhe janela + data + hora + preenche e-mail/nome da família.
 *  3. Sistema cria booking + gera link Stripe + envia por e-mail (backend).
 *  4. Consultor recebe o link pra copiar/enviar por WhatsApp também.
 *  5. Lista das reservas criadas mostra status + botão de reembolso.
 */
const isCertActiveProf = (p) => {
  if (!p?.certification_tier) return false;
  if (!["paid", "waived"].includes((p.certification_payment_status || "").toLowerCase())) return false;
  if (!p.certification_expires_at) return false;
  return new Date(p.certification_expires_at) > new Date();
};

const ConsultorAgendaWidget = () => {
  const [profs, setProfs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedProf, setSelectedProf] = useState(null);
  const [showAllProfs, setShowAllProfs] = useState(false);
  const [onlyCertified, setOnlyCertified] = useState(false);
  const [refunding, setRefunding] = useState(null);

  const loadBookings = () => api.get("/consultant/bookings").then((r) => setBookings(r.data || [])).catch(() => {});

  useEffect(() => {
    // Consultor tem endpoint próprio que devolve TODOS os aprovados (com ou
    // sem selo). A vitrine pública `/professionals` só devolveria os certificados.
    api.get("/consultant/professionals").then((r) => setProfs(r.data || []))
      .catch(() => api.get("/professionals").then((r) => setProfs(r.data || [])));
    loadBookings();
  }, []);

  const refund = async (id) => {
    if (!window.confirm("Emitir reembolso Stripe pra esta reserva? A família receberá o valor no cartão em até 72h úteis.")) return;
    setRefunding(id);
    try {
      await api.post(`/consultant/bookings/${id}/refund`);
      toast.success("Reembolso enviado ao Stripe");
      loadBookings();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro no reembolso"); }
    finally { setRefunding(null); }
  };

  return (
    <section className="space-y-5" data-testid="consultor-agenda-widget">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-display font-bold text-lg text-[#0B2B62] inline-flex items-center gap-2">
            <Calendar className="w-5 h-5 text-emerald-700" /> Agendar consulta com profissional
          </h3>
          <p className="text-xs text-slate-500 mt-1 text-left">
            Selecione o profissional, escolha um horário na agenda dele e crie a reserva. O sistema gera o link Stripe automaticamente e envia por e-mail à família — sem PIX pessoal.
          </p>
        </div>
        <button onClick={loadBookings} className="text-xs text-slate-500 hover:text-[#0B2B62] inline-flex items-center gap-1">
          <RefreshCcw className="w-3.5 h-3.5" /> Atualizar
        </button>
      </div>

      {/* Passo 1 — Escolher profissional */}
      <div>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">1 · Profissional</div>
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none"
            data-testid="agenda-only-certified-toggle">
            <input type="checkbox" checked={onlyCertified} onChange={(e) => setOnlyCertified(e.target.checked)}
              className="accent-amber-600" />
            <Award className="w-3 h-3 text-amber-600" />
            Só credenciados ({profs.filter(isCertActiveProf).length}/{profs.length})
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          {(() => {
            const filtered = onlyCertified ? profs.filter(isCertActiveProf) : profs;
            const shown = showAllProfs ? filtered : filtered.slice(0, 8);
            return (
              <>
                {shown.map((p) => (
                  <button key={p.id} onClick={() => setSelectedProf(p)} data-testid={`agenda-prof-${p.id}`}
                    className={`text-xs px-3 py-1.5 rounded-full border ${selectedProf?.id === p.id ? "bg-[#0B2B62] text-white border-[#0B2B62]" : "bg-white text-slate-700 border-slate-300 hover:border-[#0B2B62]"} inline-flex items-center gap-1.5`}>
                    {p.certification_tier && <span className={`text-[8px] px-1 rounded font-black ${TIER_CHIP[p.certification_tier]}`}>{p.certification_tier}</span>}
                    {p.display_name || p.name} · {brl(p.session_value)}
                  </button>
                ))}
                {filtered.length > 8 && !showAllProfs && (
                  <button onClick={() => setShowAllProfs(true)} className="text-xs text-slate-500 underline">
                    ver todos ({filtered.length})
                  </button>
                )}
                {filtered.length === 0 && (
                  <span className="text-xs text-slate-400">
                    {onlyCertified ? "Nenhum profissional credenciado ainda — desmarque o filtro para ver todos." : "Nenhum profissional aprovado ainda."}
                  </span>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {selectedProf && <ScheduleForm prof={selectedProf} onCreated={loadBookings} />}

      {/* Lista de reservas do consultor */}
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Minhas reservas criadas</div>
        {bookings.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400 border border-dashed border-slate-300 rounded-md">Nenhuma reserva criada pelos últimos 90 dias.</div>
        ) : (
          <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Quando</th>
                  <th className="px-3 py-2 text-left">Família</th>
                  <th className="px-3 py-2 text-left">Profissional</th>
                  <th className="px-3 py-2 text-right">Valor</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {bookings.map((b) => {
                  const when = new Date(b.scheduled_at);
                  const past = when < new Date();
                  const refundable = ["paid", "declined_by_professional"].includes(b.status) && !b.refunded_at;
                  return (
                    <tr key={b.id} data-testid={`consultor-booking-${b.id}`} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-xs">
                        {when.toLocaleString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td className="px-3 py-2 text-xs">{b.family_name || b.family_email || "—"}</td>
                      <td className="px-3 py-2 text-xs">{b.professional_name}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{brl(b.session_value)}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                          b.status === "paid" ? "bg-emerald-100 text-emerald-800" :
                          b.status === "completed" ? "bg-slate-100 text-slate-700" :
                          b.status === "refunded" ? "bg-blue-100 text-blue-800" :
                          b.status === "declined_by_professional" ? "bg-orange-100 text-orange-800" :
                          "bg-amber-100 text-amber-800"
                        }`}>{b.status}</span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-1">
                          {b.status === "paid" && !past && (
                            <a href={`/consulta/${b.id}`} target="_blank" rel="noreferrer"
                              className="text-emerald-700 hover:bg-emerald-700 hover:text-white p-1.5 rounded-md border border-emerald-500/20"
                              title="Entrar na sala de vídeo">
                              <Video className="w-3.5 h-3.5" />
                            </a>
                          )}
                          {refundable && (
                            <button onClick={() => refund(b.id)} disabled={refunding === b.id}
                              data-testid={`consultor-refund-${b.id}`}
                              className="text-blue-600 hover:bg-blue-600 hover:text-white p-1.5 rounded-md border border-blue-500/30 transition-colors disabled:opacity-50"
                              title="Emitir reembolso Stripe (3+72h úteis)">
                              {refunding === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

const ScheduleForm = ({ prof, onCreated }) => {
  const [f, setF] = useState({
    familyEmail: "", familyName: "",
    date: "", time: "",
    notes: "",
  });
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [availData, setAvailData] = useState(null);      // {slots, holiday, timezone, session_minutes}
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [policies, setPolicies] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  // Detalhes do profissional (agenda) — o admin_professionals já traz availability
  const slots = prof?.availability || [];

  // Busca slots livres quando a data é escolhida
  useEffect(() => {
    if (!f.date || !prof?.id) { setAvailData(null); return; }
    setLoadingSlots(true);
    setF((s) => ({ ...s, time: "" }));   // limpa hora ao trocar data
    api.get(`/consultant/professionals/${prof.id}/available-slots?date=${f.date}`)
      .then((r) => setAvailData(r.data))
      .catch(() => setAvailData(null))
      .finally(() => setLoadingSlots(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.date, prof?.id]);

  // Preview do horário convertido (BRT/UTC)
  const preview = React.useMemo(() => {
    if (!f.date || !f.time) return null;
    try {
      const local = new Date(`${f.date}T${f.time}:00`);
      if (isNaN(local.getTime())) return null;
      const utc = local.toISOString();
      const tz = availData?.timezone || "America/Sao_Paulo";
      const localFmt = local.toLocaleString("pt-BR", { timeZone: tz,
        weekday: "long", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
      const utcFmt = new Date(utc).toLocaleString("pt-BR", { timeZone: "UTC",
        day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
      return { localFmt, utcFmt, tz };
    } catch { return null; }
  }, [f.date, f.time, availData?.timezone]);

  const create = async () => {
    if (!f.familyEmail || !f.date || !f.time) {
      toast.error("Preencha e-mail da família, data e horário");
      return;
    }
    if (availData?.holiday) {
      toast.error(`Data é feriado nacional (${availData.holiday}). Escolha outro dia.`);
      return;
    }
    if (!termsAccepted) {
      toast.error("Você precisa confirmar que apresentou as regras à família antes de reservar.");
      return;
    }
    // Constrói ISO local → UTC
    const local = new Date(`${f.date}T${f.time}:00`);
    const iso = local.toISOString();
    setCreating(true);
    setResult(null);
    try {
      const r = await api.post("/consultant/bookings/create", {
        professional_id: prof.id,
        family_email: f.familyEmail,
        family_name: f.familyName,
        scheduled_at: iso,
        origin_url: window.location.origin,
        notes: f.notes,
        family_terms_accepted: true,
      });
      setResult(r.data);
      toast.success("Reserva criada! Link Stripe enviado por e-mail — copie abaixo pra mandar por WhatsApp também.");
      onCreated && onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setCreating(false); }
  };

  const openTerms = async () => {
    setTermsOpen(true);
    if (!policies) {
      try {
        const r = await api.get("/public/policies/consultation");
        setPolicies(r.data);
      } catch (e) { toast.error("Não foi possível carregar as regras"); }
    }
  };

  const copyLink = () => {
    if (!result?.checkout_url) return;
    navigator.clipboard.writeText(result.checkout_url).then(() =>
      toast.success("Link copiado — cole no WhatsApp da família"));
  };

  return (
    <div className="border border-emerald-200 bg-emerald-50/40 rounded-xl p-4 space-y-3" data-testid="agenda-form">
      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
        2 · Agenda de {prof.display_name || prof.name} · {brl(prof.session_value)}
      </div>
      {slots.length === 0 ? (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">Este profissional ainda não configurou agenda. Peça pra ele entrar em /profissional e cadastrar as janelas semanais.</div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {slots.map((s, i) => (
            <span key={i} className="text-[10px] px-2 py-1 rounded bg-white border border-slate-200">
              {WEEKDAYS[s.weekday]} · {s.start}–{s.end}
            </span>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">E-mail da família *</span>
          <input type="email" value={f.familyEmail} onChange={(e) => set("familyEmail", e.target.value)}
            data-testid="agenda-email"
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" placeholder="familia@..." />
        </label>
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Nome da família</span>
          <input value={f.familyName} onChange={(e) => set("familyName", e.target.value)}
            data-testid="agenda-name"
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
        </label>
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Data *</span>
          <input type="date" value={f.date} onChange={(e) => set("date", e.target.value)}
            data-testid="agenda-date"
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
        </label>
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
            Horário * {availData?.session_minutes ? <span className="text-slate-400 normal-case">({availData.session_minutes}min por consulta)</span> : null}
          </span>
          {!f.date ? (
            <input type="time" disabled placeholder="Escolha a data primeiro"
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-slate-50 text-slate-400"
              data-testid="agenda-time-placeholder" />
          ) : loadingSlots ? (
            <div className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-slate-50 text-slate-500 inline-flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Buscando slots livres…
            </div>
          ) : availData?.holiday ? (
            <div className="mt-1 w-full px-3 py-2 border border-red-300 rounded-md text-sm bg-red-50 text-red-800 font-semibold"
                 data-testid="agenda-holiday-badge">
              🎉 Feriado nacional: {availData.holiday}
            </div>
          ) : !(availData?.slots || []).length ? (
            <div className="mt-1 w-full px-3 py-2 border border-amber-300 rounded-md text-sm bg-amber-50 text-amber-800"
                 data-testid="agenda-no-slots">
              Sem slots livres nesta data (fora da agenda do profissional ou já reservados)
            </div>
          ) : (
            <select value={f.time} onChange={(e) => set("time", e.target.value)}
              data-testid="agenda-time"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
              <option value="">Escolha um horário livre</option>
              {(availData?.slots || []).map((s) => (
                <option key={s} value={s} data-testid={`agenda-slot-${s}`}>{s}</option>
              ))}
            </select>
          )}
        </label>
      </div>
      {preview && !availData?.holiday && (
        <div className="p-2.5 rounded-md border border-emerald-200 bg-emerald-50/60 text-[11px] text-emerald-900 leading-relaxed"
             data-testid="agenda-preview">
          <b>Preview:</b> Consulta será {preview.localFmt} <span className="text-emerald-800">({preview.tz})</span>
          <span className="text-emerald-700"> · UTC: {preview.utcFmt}</span>
        </div>
      )}
      <label className="block">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Motivo (curto — visível ao profissional)</span>
        <input value={f.notes} onChange={(e) => set("notes", e.target.value)}
          data-testid="agenda-notes"
          className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
          placeholder="Ex.: primeira consulta, retorno, avaliação inicial" />
      </label>

      {/* Iter 68.50 — Aceite obrigatório das regras */}
      <div className="p-3 rounded-md border border-amber-300 bg-amber-50/60" data-testid="agenda-terms-block">
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            data-testid="agenda-terms-check"
            className="mt-1" />
          <div className="text-[11px] text-amber-900 leading-relaxed">
            <b>Confirmo que apresentei à família as regras da consulta</b> e obtive consentimento verbal/escrito:
            no-show 50/50, cancelamento até 24h antes com reembolso integral, feriados bloqueados, fuso do profissional.
            {" "}
            <button type="button" onClick={openTerms} data-testid="agenda-terms-open"
              className="underline font-bold text-amber-900 hover:text-amber-700">
              Ver política completa (versão que a família aceita)
            </button>.
          </div>
        </label>
      </div>

      <button onClick={create} disabled={creating || !termsAccepted} data-testid="agenda-create-btn"
        className="w-full text-sm text-white bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 px-4 py-2 rounded-md inline-flex items-center justify-center gap-1.5 font-bold">
        {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        Criar reserva e gerar link Stripe
      </button>

      {/* Modal com termos completos */}
      {termsOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setTermsOpen(false)}
             data-testid="agenda-terms-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-xl text-[#0B2B62]">Política da Consulta RAF Direct</h3>
              <button onClick={() => setTermsOpen(false)} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">×</button>
            </div>
            {!policies ? (
              <div className="text-slate-500 text-sm py-8 text-center">
                <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Carregando…
              </div>
            ) : (
              <>
                <p className="text-xs text-slate-500 mb-3">Versão <code className="bg-slate-100 px-1 rounded">{policies.version}</code> · atualizada em {policies.updated_at}</p>
                <div className="text-sm text-slate-700 space-y-2 leading-relaxed">
                  <p className="font-bold text-[#0B2B62]">Resumo em 1 minuto:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    {policies.summary.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                  <div className="mt-4 space-y-3">
                    {Object.entries(policies.sections).map(([k, v]) => (
                      <div key={k} className="border-l-2 border-emerald-500 pl-3">
                        <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-bold">{k.replace(/_/g, " ")}</div>
                        <p className="text-slate-700">{v}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <button onClick={() => { setTermsAccepted(true); setTermsOpen(false); toast.success("Aceite registrado."); }}
                  data-testid="agenda-terms-accept-btn"
                  className="mt-5 w-full py-2 bg-emerald-700 hover:bg-emerald-800 text-white font-bold rounded-md">
                  A família concorda com estes termos
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {result?.checkout_url && (
        <div className="p-3 bg-white border border-emerald-300 rounded-md space-y-2">
          <div className="text-xs font-bold text-emerald-800">Link de pagamento criado!</div>
          <div className="p-2 bg-slate-50 border border-slate-200 rounded font-mono text-[10px] break-all">{result.checkout_url}</div>
          <div className="flex gap-2">
            <button onClick={copyLink} data-testid="agenda-copy-link"
              className="flex-1 text-xs px-3 py-1.5 border border-emerald-500 text-emerald-700 rounded-md hover:bg-emerald-500 hover:text-white inline-flex items-center justify-center gap-1">
              <Copy className="w-3 h-3" /> Copiar link
            </button>
            <a href={`https://wa.me/?text=${encodeURIComponent(`Olá! Aqui está o link seguro pra pagar sua consulta na RAF Direct: ${result.checkout_url}`)}`}
              target="_blank" rel="noreferrer"
              className="flex-1 text-xs px-3 py-1.5 bg-emerald-700 text-white rounded-md hover:bg-emerald-800 text-center">
              Enviar por WhatsApp
            </a>
          </div>
          <div className="text-[10px] text-slate-500">Também enviamos o link por e-mail para {result.family_email || "a família"} — o consultor recebe 10% dos 35% RAF quando a família pagar.</div>
        </div>
      )}
    </div>
  );
};

export default ConsultorAgendaWidget;
